import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchDocuments, uploadDocument, deleteDocument, logout } from '../api/api';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  AppBar,
  Toolbar,
  IconButton,
  Input,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';

interface ExtractedField {
  id: number;
  field_name: string;
  field_value: string;
  confidence: number;
}

interface Document {
  id: number;
  title: string;
  pdf_file: string;
  uploaded_at: string;
  is_processed: boolean;
  extracted_text: string;
  extracted_fields: ExtractedField[];
}

const DocumentList: React.FC = () => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const response = await fetchDocuments();
      setDocuments(response.data);
    } catch (err) {
      setError('Fehler beim Laden der Dokumente');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('pdf_file', file);
    formData.append('title', file.name);

    try {
      setLoading(true);
      setError('');
      await uploadDocument(formData);
      setSuccess('PDF erfolgreich hochgeladen und verarbeitet!');
      loadDocuments();
    } catch (err) {
      setError('Fehler beim Hochladen der Datei');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Möchten Sie dieses Dokument wirklich löschen?')) return;

    try {
      await deleteDocument(id);
      setSuccess('Dokument gelöscht');
      loadDocuments();
    } catch (err) {
      setError('Fehler beim Löschen');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            PDF Tätigkeitsnachweise Scanner
          </Typography>
          <IconButton color="inherit" onClick={handleLogout}>
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container sx={{ mt: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Meine Dokumente
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <Button
            variant="contained"
            component="label"
            startIcon={<UploadFileIcon />}
            disabled={loading}
          >
            PDF hochladen
            <Input
              type="file"
              inputProps={{ accept: '.pdf' }}
              onChange={handleUpload}
              sx={{ display: 'none' }}
            />
          </Button>
        </Box>

        {loading && <CircularProgress />}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          {documents.map((doc) => (
            <Card key={doc.id}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {doc.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Hochgeladen: {new Date(doc.uploaded_at).toLocaleString('de-DE')}
                  </Typography>
                  
                  <Box sx={{ mt: 2 }}>
                    {doc.is_processed ? (
                      <Chip label="Verarbeitet" color="success" size="small" />
                    ) : (
                      <Chip label="In Bearbeitung..." color="warning" size="small" />
                    )}
                  </Box>

                  {doc.extracted_fields.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Extrahierte Daten:
                      </Typography>
                      {doc.extracted_fields.map((field) => (
                        <Typography key={field.id} variant="body2">
                          <strong>{field.field_name}:</strong> {field.field_value}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </CardContent>
                <CardActions>
                  <Button 
                    size="small" 
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDelete(doc.id)}
                  >
                    Löschen
                  </Button>
                </CardActions>
              </Card>
          ))}
        </Box>

        {documents.length === 0 && !loading && (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography color="text.secondary">
              Noch keine Dokumente hochgeladen. Laden Sie Ihr erstes PDF hoch!
            </Typography>
          </Box>
        )}
      </Container>
    </>
  );
};

export default DocumentList;
